import React, { useEffect, useState } from "react";
import { Typography, Button } from "@mui/material";
import dayjs from "configs/dayjs.config";

import { useGlobalContext } from "context/GlobalContext";
import { useProductContext } from "../context";
import { PageTitle } from "components/PageMeta/PageTitle";
import { pageName } from "constants/pageNames";
import { PAGE_NOTES } from "constants/PageNotes";
import { ProductTopBanner } from "../components/ProductTopBanner";
import { EXECUTION_TIMELINE_FIELDS } from "../constants/product.constants";
import { TextFieldWrapper } from "components/Wrappers/FormComponentsWrappers/TextFieldWrapper";
import { updateExecutionTimeline } from "API/apis/product/updateExecutionTimeline";
import { STATUS_CODES } from "constants/responses/statusCodes";
import { useNavigate } from "react-router-dom";
import { routeLinks } from "constants/paths";
import { dateTimeFormat } from "constants/componentsConstants";
import { getProductExecutionTimelineApi } from "API/apis/product/getProductExecutionTimeline";
import { message } from "constants/responses/message";
import { useParams } from "react-router-dom";
import { DatePickerWrapper } from "components/DateTimePicker/DatePicker";
import { getTaskFromLotIdApi } from "API/apis/product/getTaskFromLotId";
import { SingleSelect } from "components/Select/SingleSelect";
import { FlexBox } from "components/Wrappers/FlexBox/FlexBox";
import { getExecutionTimelineAssigneeByLotIdApi } from "API/apis/product/getExecutionTimelineAssigneeByLotId";
import { getExecutionTimelineAssigneeApi } from "API/apis/product/getExecutionTimelineAssignee";

const initialValue = {
  startDate: null,
  endDate: null,
  handover: 0,
  shopDrawing: 0,
  clientApprovalSd: 0,
  shopDrawingRevision: 0,
  clientApprovalSdr: 0,
  proto: 0,
  clientApprovalP: 0,
  protoRevision: 0,
  clientApprovalPr: 0,
  pcd: 0,
  protoProduction: 0,
  production: 0,
  specificPurchase: 0,
  buffer: 0,
};

export const EditProductExecutionTimeline = () => {
  const { id } = useParams();
  const { setIsInitializing, setError } = useGlobalContext();
  const [isReloadRequired, setIsReloadRequired] = useState(false);
  const { currentProject, setReloadRequired, reloadRequired } =
    useProductContext();
  const [formData, setFormData] = useState(initialValue);
  const [taskCheck, setTaskCheck] = useState();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    calculateFinalDate();
  }, [isReloadRequired]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsInitializing(true);
        const promises = [
          getTaskFromLotIdApi(id),
          getProductExecutionTimelineApi(id),
          getExecutionTimelineAssigneeByLotIdApi(id),
          getExecutionTimelineAssigneeApi(),
        ];
        const [tasksData, timeline, assignee, defaultAssignee] =
          await Promise.all(promises);

        if (tasksData.status !== 200 || timeline.status !== 200) {
          throw new Error(message.error.generalFailure);
        }

        let tempFormDataAssignee = {};
        assignee.data.forEach((item) => {
          tempFormDataAssignee = {
            ...tempFormDataAssignee,
            ["assignee_" + item.field]: item.userId,
          };
        });
        setAssigneeOptions(
          defaultAssignee.data.map((item) => ({
            option: item.userId,
            value: item.name,
          }))
        );
        delete timeline.data.endDate;

        setFormData({ ...timeline.data[0], ...tempFormDataAssignee });
        setStartDate(timeline.data[0].startDate);
        setTaskCheck(tasksData.data);
        setIsReloadRequired(!isReloadRequired);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsInitializing(false);
      }
    };
    fetchData();
  }, [id]);

  const calculateFinalDate = () => {
    const totalDays = Object.entries(EXECUTION_TIMELINE_FIELDS).reduce(
      (acc, [key]) => {
        return Number(acc) + Number(formData[key]);
      },
      0
    );
    setEndDate(dayjs(startDate).add(totalDays, "day").format("YYYY-MM-DD"));
  };
  const handleStartDateChange = (newValue) => {
    setStartDate(dayjs(newValue).format(dateTimeFormat.date));
    setFormData({
      ...formData,
      startDate: dayjs(newValue).format(dateTimeFormat.date),
    });
    setIsReloadRequired(!isReloadRequired);
  };
  const handleEndDateChange = (newValue) => {
    setEndDate(dayjs(newValue).format(dateTimeFormat.date));
    setFormData({
      ...formData,
      endDate: dayjs(newValue).format(dateTimeFormat.date),
    });
    setIsReloadRequired(!isReloadRequired);
  };
  const handleTextFieldChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
    setIsReloadRequired(!isReloadRequired);
  };
  const handleSingleSelect = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };
  const handleSubmit = async () => {
    const { data, status } = await updateExecutionTimeline({
      ...formData,
      startDate: dayjs(startDate).add(1).format(dateTimeFormat.date),
      endDate,
      productLotProcessingId: [id],
      isSingleUpdate: true,
    });

    if (status !== STATUS_CODES.SUCCESS) {
      throw new Error(data.message);
    }
    setReloadRequired(!reloadRequired);
    navigate(routeLinks.projectManagementProject + "/" + currentProject);
  };
  return (
    <>
      <PageTitle pageName={pageName.product.editExecutionTimeline} />
      <Typography fontStyle="italic">
        Note: {PAGE_NOTES.executionTimeline}
      </Typography>

      <ProductTopBanner buttonType="back" />
      <form onSubmit={handleSubmit}>
        <DatePickerWrapper
          label="Start Date"
          name="startDate"
          value={dayjs(startDate)}
          onChange={(newValue) => handleStartDateChange(dayjs(newValue))}
          disabled={!!taskCheck}
          // TODO
          // minDate={dayjs()}
        />
        <DatePickerWrapper
          label="End Date"
          name="endDate"
          value={dayjs(endDate)}
          onChange={(newValue) => handleEndDateChange(dayjs(newValue))}
          disabled={true}
        />
        {/* ===============================timelines============================== */}
        {Object.entries(EXECUTION_TIMELINE_FIELDS).map(
          ([key, value], index) => {
            return (
              <FlexBox key={index}>
                <TextFieldWrapper
                  id={key}
                  name={key}
                  label={value}
                  variant="outlined"
                  type="number"
                  value={formData?.[key] || 0}
                  fullWidth
                  onChange={handleTextFieldChange}
                  inputProps={{ min: 0 }}
                  disabled={!!taskCheck?.[value]}
                />
                <SingleSelect
                  label="Select Assignee"
                  name={"assignee_" + key}
                  handleSelectSingle={(e) => handleSingleSelect(e)}
                  options={assigneeOptions}
                  selectedOption={formData?.["assignee_" + key]}
                  isDisabled={!!taskCheck?.[value]}
                />
              </FlexBox>
            );
          }
        )}

        {/* ===============================timelines============================== */}
        <Button onClick={handleSubmit} variant="contained" fullWidth>
          Update
        </Button>
      </form>
    </>
  );
};
